import { apiFetch } from '../api/http';
import { toAppError } from '../utils/appErrors';
import { extendTempFleetVehicleRental, returnXRental } from './procurement';
import { getSupabaseClient } from './supabase';
import { failure as vendorRentalFailure, VendorRentalInventoryError, type BorrowedUnit } from './vendorRentalInventory';

/**
 * The Subrental record (Warehouse › Subrentals › one order): one round trip through
 * `get_subrental_order_record`, normalised defensively, plus the order-level actions. An order is a
 * cross-rental purchase order or a subrental with no purchase order (vehicles). See docs/warehouse.md.
 *
 * Every action takes a request key: one per submitted payload, reused only when that same payload is
 * retried after an unknown outcome. Errors come back as VendorRentalInventoryError with the vendor
 * rental page's readable messages; `outcome: 'unknown'` means the result needs verifying with the
 * same key.
 */

type Json = { [key: string]: unknown };

export type SubrentalRecordKind = 'event' | 'shared_stock' | 'vehicle';
export type SubrentalLineTracking = 'serial' | 'quantity';
export type SubrentalUnitPlace = 'base' | 'event' | 'vendor';

export type SubrentalVendorContact = { name: string | null; phone: string | null; email: string | null };

/** R2's own cost observation for a cross-rental (APPO / APPOLINE), shown read-only. Not a vendor bill. */
export type SubrentalR2CostBasis = {
  grossAmount: number | null;
  discountAmount: number | null;
  netAmount: number | null;
  taxAmount: number | null;
  freightAmount: number | null;
  billingUnit: string | null;
  amountBasis: string | null;
  observedAt: string | null;
};

export type SubrentalRecordOrder = {
  id: string;
  recordKind: 'order' | 'subrental';
  docNo: string | null;
  status: string;
  source: 'r2' | 'portal';
  isR2: boolean;
  kind: SubrentalRecordKind;
  vendor: { id: string | null; name: string | null; contact: SubrentalVendorContact | null } | null;
  site: { id: string; name: string | null; timezone: string | null } | null;
  windowStartAt: string | null;
  windowEndAt: string | null;
  days: number | null;
  dueBackInDays: number | null;
  daysLate: number | null;
  extended: boolean;
  cost: {
    mode: 'order_total' | 'per_day';
    perDay: number | null;
    estimate: boolean;
    /** The order or estimate total when the reader sends one. */
    total: number | null;
    billed: number | null;
    credits: number | null;
  };
  eventCount: number;
  notes: string | null;
  expectedAt: string | null;
  r2SyncedAt: string | null;
  r2CostBasis: SubrentalR2CostBasis | null;
  /** The temporary fleet vehicle a vehicle subrental is linked to (when the reader sends it). */
  vehicleId: string | null;
};

export type SubrentalRecordLine = {
  id: string;
  subrentalId: string | null;
  item: { id: string | null; code: string | null; name: string | null } | null;
  description: string | null;
  tracking: SubrentalLineTracking;
  /** Ordered. */
  qty: number;
  /** Incoming units cancelled before they came (Cancel incoming): no longer due. */
  cancelled: number;
  /** Still due from the vendor, as Receive checks it; null when the reader does not say. */
  outstanding: number | null;
  received: number;
  /** Out with events now (a return from an event nets its pick out). */
  withEvents: number;
  /** At the base, ready to go back to the vendor, as Return to vendor checks it; null when unknown. */
  atBase: number | null;
  returned: number;
  ratePerDay: number | null;
  lineTotal: number | null;
  status: string;
  vehicleId: string | null;
  r2CostBasis: SubrentalR2CostBasis | null;
};

export type SubrentalRecordUnit = {
  id: string;
  /** The Portal label (the scannable `VR-…` barcode). */
  label: string | null;
  vendorTag: string | null;
  itemName: string | null;
  /** The rental line the unit belongs to, when the reader sends it. */
  subrentalId: string | null;
  where: { kind: SubrentalUnitPlace; name: string | null; event: { id: string; code: string | null } | null };
  status: string;
};

export type SubrentalUnitCounts = { incoming: number; atBase: number; withEvents: number; returned: number };

export type SubrentalCostTerm = {
  id: string;
  subrentalId: string | null;
  label: string | null;
  from: string | null;
  through: string | null;
  basis: string | null;
  quantity: number | null;
  unitCost: number | null;
  perDay: number | null;
  currency: string;
};

export type SubrentalCostEntry = {
  id: string;
  subrentalId: string | null;
  kind: string | null;
  reference: string | null;
  from: string | null;
  through: string | null;
  amount: number | null;
  currency: string;
  recordedAt: string | null;
};

export type SubrentalPerformance = {
  vendorCostForecast: number | null;
  vendorCostSoFar: number | null;
  unitDaysForecast: number | null;
  unitDaysSoFar: number | null;
  revenueForecast: number | null;
  revenueSoFar: number | null;
};

export type SubrentalTimelineEntry = {
  id: string;
  at: string | null;
  kind: string;
  summary: string;
  actor: { id: string | null; name: string | null } | null;
  details: Json | null;
};

export const SUBRENTAL_ACTION_KEYS = [
  'receive',
  'return_vendor',
  'extend',
  'change_dates',
  'cancel_incoming',
  'labels',
  'add_line',
  'record_bill',
  'record_credit',
  'edit_rates',
] as const;
export type SubrentalActionKey = (typeof SUBRENTAL_ACTION_KEYS)[number];
export type SubrentalActionGate = { allowed: boolean; reason: string | null };
export type SubrentalRecordActions = { [key in SubrentalActionKey]: SubrentalActionGate };

export type SubrentalRecord = {
  order: SubrentalRecordOrder;
  lines: SubrentalRecordLine[];
  units: SubrentalRecordUnit[];
  unitCounts: SubrentalUnitCounts;
  costTerms: SubrentalCostTerm[];
  costEntries: SubrentalCostEntry[];
  performance: SubrentalPerformance | null;
  timeline: SubrentalTimelineEntry[];
  actions: SubrentalRecordActions;
};

export type SubrentalRecordResult =
  | { kind: 'record'; record: SubrentalRecord }
  | { kind: 'redirect'; target: 'purchase_order'; id: string }
  | { kind: 'not_found' };

export const subrentalRecordKey = (recordId: string) => ['warehouse', 'subrental-record', recordId] as const;

function asObject(value: unknown): Json | null {
  return value && typeof value === 'object' && !Array.isArray(value) ? value as Json : null;
}

function asObjects(value: unknown): Json[] {
  return Array.isArray(value) ? value.map(asObject).filter((row): row is Json => row != null) : [];
}

function asText(value: unknown): string | null {
  if (value == null) return null;
  const text = String(value);
  return text.length > 0 ? text : null;
}

function asNumber(value: unknown, fallback = 0): number {
  const number = typeof value === 'number' ? value : Number(value ?? NaN);
  return Number.isFinite(number) ? number : fallback;
}

function asNullableNumber(value: unknown): number | null {
  if (value == null || value === '') return null;
  const number = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(number) ? number : null;
}

function asStatus(value: unknown): string {
  return String(value ?? '').trim().toLowerCase();
}

function firstText(object: Json | null, keys: readonly string[]): string | null {
  if (!object) return null;
  for (const key of keys) {
    const text = asText(object[key]);
    if (text) return text;
  }
  return null;
}

function firstNumber(object: Json | null, keys: readonly string[]): number | null {
  if (!object) return null;
  for (const key of keys) {
    const number = asNullableNumber(object[key]);
    if (number != null) return number;
  }
  return null;
}

function parseCostBasis(value: unknown): SubrentalR2CostBasis | null {
  const basis = asObject(value);
  if (!basis) return null;
  return {
    grossAmount: firstNumber(basis, ['gross_amount', 'gross']),
    discountAmount: firstNumber(basis, ['discount_amount', 'supplier_discount_amount', 'discount']),
    netAmount: firstNumber(basis, ['net_amount', 'net']),
    taxAmount: firstNumber(basis, ['tax_amount', 'tax']),
    freightAmount: firstNumber(basis, ['freight_amount', 'freight']),
    billingUnit: firstText(basis, ['billing_unit', 'unit']),
    amountBasis: firstText(basis, ['amount_basis']),
    observedAt: firstText(basis, ['observed_at']),
  };
}

function parseOrder(raw: Json): SubrentalRecordOrder | null {
  const id = asText(raw.id);
  if (!id) return null;
  const isR2 = raw.is_r2 === true || raw.source === 'r2';
  const vendor = asObject(raw.vendor);
  const contact = asObject(vendor?.contact);
  const site = asObject(raw.site);
  const siteId = asText(site?.id);
  const cost = asObject(raw.cost) ?? {};
  const vehicle = asObject(raw.vehicle);
  return {
    id,
    recordKind: raw.record_kind === 'subrental' ? 'subrental' : 'order',
    docNo: asText(raw.doc_no),
    status: asStatus(raw.status),
    source: isR2 ? 'r2' : 'portal',
    isR2,
    kind: raw.kind === 'vehicle' ? 'vehicle' : raw.kind === 'event' ? 'event' : 'shared_stock',
    vendor: vendor
      ? {
        id: asText(vendor.id),
        name: asText(vendor.name),
        contact: contact
          ? { name: asText(contact.name), phone: asText(contact.phone), email: asText(contact.email) }
          : null,
      }
      : null,
    site: siteId ? { id: siteId, name: asText(site?.name), timezone: asText(site?.timezone) } : null,
    windowStartAt: asText(raw.window_start_at),
    windowEndAt: asText(raw.window_end_at),
    days: asNullableNumber(raw.days),
    dueBackInDays: asNullableNumber(raw.due_back_in_days),
    daysLate: asNullableNumber(raw.days_late),
    extended: raw.extended === true,
    cost: {
      mode: cost.mode === 'per_day' ? 'per_day' : 'order_total',
      perDay: asNullableNumber(cost.per_day),
      estimate: cost.estimate === true,
      total: firstNumber(cost, ['total', 'amount', 'estimate_total', 'order_total']),
      billed: asNullableNumber(cost.billed),
      credits: asNullableNumber(cost.credits),
    },
    eventCount: asNumber(raw.event_count),
    notes: asText(raw.notes),
    expectedAt: firstText(raw, ['expected_at', 'expected_arrival_at', 'expected_delivery_at']),
    r2SyncedAt: firstText(raw, ['r2_synced_at', 'last_synced_at']),
    r2CostBasis: parseCostBasis(raw.r2_cost_basis),
    vehicleId: asText(vehicle?.id) ?? firstText(raw, ['vehicle_id', 'linked_temp_vehicle_id']),
  };
}

function parseLine(raw: Json): SubrentalRecordLine | null {
  const id = asText(raw.id);
  if (!id) return null;
  const item = asObject(raw.item);
  const hasItem = Boolean(item && (asText(item.id) || asText(item.code) || asText(item.name)));
  return {
    id,
    subrentalId: asText(raw.subrental_id),
    item: hasItem && item ? { id: asText(item.id), code: asText(item.code), name: asText(item.name) } : null,
    description: asText(raw.description),
    tracking: raw.tracking === 'serial' || raw.tracking === 'unit' ? 'serial' : 'quantity',
    qty: asNumber(raw.qty),
    cancelled: asNumber(raw.cancelled),
    outstanding: asNullableNumber(raw.outstanding),
    received: asNumber(raw.received),
    withEvents: asNumber(raw.with_events),
    atBase: asNullableNumber(raw.at_base),
    returned: asNumber(raw.returned),
    ratePerDay: asNullableNumber(raw.rate_per_day),
    lineTotal: asNullableNumber(raw.line_total),
    status: asStatus(raw.status),
    vehicleId: firstText(raw, ['vehicle_id', 'linked_temp_vehicle_id', 'temp_fleet_vehicle_id']),
    r2CostBasis: parseCostBasis(raw.r2_cost_basis),
  };
}

function parseUnit(raw: Json): SubrentalRecordUnit | null {
  const id = asText(raw.id) ?? asText(raw.unit_id);
  if (!id) return null;
  const where = asObject(raw.where);
  const event = asObject(where?.event);
  const eventId = asText(event?.id);
  return {
    id,
    label: firstText(raw, ['label', 'portal_label', 'portal_barcode']),
    // Vendor tags keep their exact text: leading zeros and padding are part of the tag.
    vendorTag: asText(raw.vendor_tag),
    itemName: asText(raw.item_name),
    subrentalId: firstText(raw, ['subrental_id', 'line_subrental_id']),
    where: {
      kind: where?.kind === 'event' ? 'event' : where?.kind === 'vendor' ? 'vendor' : 'base',
      name: asText(where?.name),
      event: eventId ? { id: eventId, code: asText(event?.code) } : null,
    },
    status: asStatus(raw.status),
  };
}

function parseActions(value: unknown): SubrentalRecordActions {
  const object = asObject(value) ?? {};
  const actions = {} as SubrentalRecordActions;
  for (const key of SUBRENTAL_ACTION_KEYS) {
    const gate = asObject(object[key]);
    actions[key] = { allowed: gate?.allowed === true, reason: asText(gate?.reason) };
  }
  return actions;
}

function parseCostTerm(raw: Json, index: number): SubrentalCostTerm {
  return {
    id: firstText(raw, ['id', 'term_id']) ?? `term-${index}`,
    subrentalId: asText(raw.subrental_id),
    label: firstText(raw, ['label', 'version_label', 'name']),
    from: firstText(raw, ['from', 'start_date', 'effective_from']),
    through: firstText(raw, ['through', 'end_date', 'effective_to']),
    basis: firstText(raw, ['basis', 'billing_period']),
    quantity: firstNumber(raw, ['quantity', 'qty']),
    unitCost: firstNumber(raw, ['unit_cost']),
    perDay: firstNumber(raw, ['per_day', 'daily_cost']),
    currency: asText(raw.currency) ?? 'USD',
  };
}

function parseCostEntry(raw: Json, index: number): SubrentalCostEntry {
  return {
    id: firstText(raw, ['id', 'entry_id']) ?? `entry-${index}`,
    subrentalId: asText(raw.subrental_id),
    kind: firstText(raw, ['kind', 'entry_kind']),
    reference: asText(raw.reference),
    from: firstText(raw, ['from', 'start_date']),
    through: firstText(raw, ['through', 'end_date']),
    amount: firstNumber(raw, ['amount', 'net_amount']),
    currency: asText(raw.currency) ?? 'USD',
    recordedAt: firstText(raw, ['recorded_at', 'created_at', 'at']),
  };
}

function parsePerformance(value: unknown): SubrentalPerformance | null {
  const object = asObject(value);
  if (!object) return null;
  return {
    vendorCostForecast: asNullableNumber(object.vendor_cost_forecast),
    vendorCostSoFar: asNullableNumber(object.vendor_cost_so_far),
    unitDaysForecast: asNullableNumber(object.unit_days_forecast),
    unitDaysSoFar: asNullableNumber(object.unit_days_so_far),
    revenueForecast: asNullableNumber(object.revenue_forecast),
    revenueSoFar: asNullableNumber(object.revenue_so_far),
  };
}

function parseTimelineEntry(raw: Json, index: number): SubrentalTimelineEntry {
  const actor = asObject(raw.actor);
  const actorName = asText(actor?.name);
  const actorId = asText(actor?.id);
  return {
    id: asText(raw.id) ?? `timeline-${index}`,
    at: asText(raw.at),
    kind: asStatus(raw.kind) || 'note',
    summary: asText(raw.summary) ?? '',
    actor: actorName || actorId ? { id: actorId, name: actorName } : null,
    details: asObject(raw.details),
  };
}

function unitCountsFrom(value: unknown, units: SubrentalRecordUnit[]): SubrentalUnitCounts {
  const object = asObject(value);
  if (object) {
    return {
      incoming: asNumber(object.incoming),
      atBase: asNumber(object.at_base),
      withEvents: asNumber(object.with_events),
      returned: asNumber(object.returned),
    };
  }
  return {
    incoming: units.filter((unit) => unit.status === 'expected').length,
    atBase: units.filter((unit) => unit.status === 'received').length,
    withEvents: units.filter((unit) => unit.status === 'issued').length,
    returned: units.filter((unit) => unit.status === 'returned').length,
  };
}

/** One record round trip. A missing record is `not_found` (P0002), never an error. */
export async function fetchSubrentalRecord(recordId: string): Promise<SubrentalRecordResult> {
  const { data, error } = await getSupabaseClient().rpc('get_subrental_order_record', { p_po_id: recordId });
  if (error) {
    if (error.code === 'P0002') return { kind: 'not_found' };
    throw error;
  }
  const root = asObject(data);
  if (!root) return { kind: 'not_found' };
  const redirect = asObject(root.redirect);
  const redirectId = asText(redirect?.id);
  if (redirect && redirectId && redirect.kind === 'purchase_order') {
    return { kind: 'redirect', target: 'purchase_order', id: redirectId };
  }
  const order = parseOrder(asObject(root.order) ?? {});
  if (!order) throw new Error('The subrental record could not be read. Refresh and try again.');
  const units = asObjects(root.units).map(parseUnit).filter((unit): unit is SubrentalRecordUnit => unit != null);
  return {
    kind: 'record',
    record: {
      order,
      lines: asObjects(root.lines).map(parseLine).filter((line): line is SubrentalRecordLine => line != null),
      units,
      unitCounts: unitCountsFrom(root.unit_counts, units),
      costTerms: asObjects(root.cost_terms).map(parseCostTerm),
      costEntries: asObjects(root.cost_entries).map(parseCostEntry),
      performance: parsePerformance(root.performance),
      timeline: asObjects(root.timeline).map(parseTimelineEntry),
      actions: parseActions(root.actions),
    },
  };
}

// ---------------------------------------------------------------------------------------------
// Order actions (Portal subrentals; R2 cross-rentals are refused by the server)
// ---------------------------------------------------------------------------------------------

export type SubrentalOrderActionResult = {
  orderId: string | null;
  replayed: boolean;
  isR2: boolean;
  results: Array<{ subrentalId: string; appliedQuantity: number; unitIds: string[]; status: string | null }>;
};

function parseActionResult(data: unknown): SubrentalOrderActionResult {
  const object = asObject(data);
  if (!object || object.ok === false) {
    throw new VendorRentalInventoryError('The result needs verification. Retry this same vendor operation.', 'unknown');
  }
  return {
    orderId: asText(object.order_id) ?? asText(object.id),
    replayed: object.replayed === true,
    isR2: object.is_r2 === true,
    results: asObjects(object.results).map((row) => ({
      subrentalId: asText(row.subrental_id) ?? '',
      appliedQuantity: asNumber(row.applied_quantity),
      unitIds: Array.isArray(row.unit_ids) ? row.unit_ids.map((id) => String(id)) : [],
      status: asText(row.status),
    })),
  };
}

async function callOrderAction(name: string, args: Json): Promise<SubrentalOrderActionResult> {
  let data: unknown;
  try {
    const response = await getSupabaseClient().rpc(name, args);
    if (response.error) throw response.error;
    data = response.data;
  } catch (error) {
    if (error instanceof VendorRentalInventoryError) throw error;
    throw vendorRentalFailure(error);
  }
  return parseActionResult(data);
}

function noteOrNull(note: string | null | undefined): string | null {
  const text = note?.trim();
  return text ? text : null;
}

export type SubrentalReceiveOp = {
  subrentalId: string;
  quantity: number;
  /** Unit-tracked lines: one entry per unit received (an expected unit, or a vendor tag, or neither). */
  units?: Array<{ unitId?: string; vendorBarcode?: string | null }>;
};

export function receiveSubrentalOrder(input: {
  orderId: string;
  requestKey: string;
  ops: SubrentalReceiveOp[];
  note?: string | null;
}): Promise<SubrentalOrderActionResult> {
  return callOrderAction('subrental_order_receive', {
    p_order_id: input.orderId,
    p_request_key: input.requestKey,
    p_ops: input.ops.map((op) => ({
      subrental_id: op.subrentalId,
      quantity: op.quantity,
      ...(op.units ? { units: op.units } : {}),
    })),
    p_note: noteOrNull(input.note),
  });
}

export type SubrentalReturnOp = { subrentalId: string; quantity: number; unitIds?: string[] };

export function returnSubrentalOrder(input: {
  orderId: string;
  requestKey: string;
  ops: SubrentalReturnOp[];
  note?: string | null;
}): Promise<SubrentalOrderActionResult> {
  return callOrderAction('subrental_order_return', {
    p_order_id: input.orderId,
    p_request_key: input.requestKey,
    p_ops: input.ops.map((op) => ({
      subrental_id: op.subrentalId,
      quantity: op.quantity,
      ...(op.unitIds ? { unitIds: op.unitIds } : {}),
    })),
    p_note: noteOrNull(input.note),
  });
}

/** Extends or changes the return time of every open equipment line on the order. */
export function reviseSubrentalOrderWindow(input: {
  orderId: string;
  requestKey: string;
  endAt: string;
  reason: string;
}): Promise<SubrentalOrderActionResult> {
  return callOrderAction('subrental_order_revise_window', {
    p_order_id: input.orderId,
    p_request_key: input.requestKey,
    p_end_at: input.endAt,
    p_reason: input.reason.trim(),
  });
}

export function cancelSubrentalIncoming(input: {
  orderId: string;
  requestKey: string;
  ops: Array<{ subrentalId: string; quantity: number }>;
  reason: string;
}): Promise<SubrentalOrderActionResult> {
  return callOrderAction('subrental_order_cancel_incoming', {
    p_order_id: input.orderId,
    p_request_key: input.requestKey,
    p_ops: input.ops.map((op) => ({ subrental_id: op.subrentalId, quantity: op.quantity })),
    p_reason: input.reason.trim(),
  });
}

/**
 * "Report a problem" (also on R2 cross-rentals). An order record is its cross-rental purchase
 * order; a PO-less subrental is itself.
 */
export async function reportSubrentalProblem(input: {
  recordKind: 'order' | 'subrental';
  recordId: string;
  requestKey: string;
  notes: string;
  details?: Json | null;
}): Promise<void> {
  try {
    const { data, error } = await getSupabaseClient().rpc('procurement_record_add_event', {
      p_kind: input.recordKind === 'order' ? 'purchase_order' : 'subrental',
      p_record_id: input.recordId,
      p_request_key: input.requestKey,
      p_event_type: 'problem_reported',
      p_notes: input.notes.trim(),
      p_details: input.details ?? null,
    });
    if (error) throw error;
    if (asObject(data)?.ok === false) throw new Error('The problem was not saved. Try again.');
  } catch (error) {
    if (error instanceof VendorRentalInventoryError) throw error;
    throw vendorRentalFailure(error);
  }
}

// ---------------------------------------------------------------------------------------------
// Vehicles keep their temporary-fleet calls
// ---------------------------------------------------------------------------------------------

/** The temporary fleet vehicle linked to a vehicle subrental (when the record does not carry it). */
export async function fetchSubrentalVehicleId(subrentalId: string): Promise<string | null> {
  const { data, error } = await getSupabaseClient()
    .from('subrentals')
    .select('linked_temp_vehicle_id')
    .eq('subrental_id', subrentalId)
    .maybeSingle();
  if (error) throw error;
  return asText(asObject(data)?.linked_temp_vehicle_id);
}

export async function returnVehicleSubrental(subrentalId: string): Promise<void> {
  await returnXRental(subrentalId);
}

export async function extendVehicleSubrental(input: { vehicleId: string; newEndAt: string; reason: string }): Promise<void> {
  await extendTempFleetVehicleRental({ vehicleId: input.vehicleId, newEndAt: input.newEndAt, reason: input.reason.trim() || null });
}

export type VehicleSubrentalLine = {
  description: string;
  qty: number;
  dailyCost: number | null;
  startAt: string;
  endAt: string;
  notes: string | null;
  vehicleInfo: { name: string; vehicleType: string | null; licensePlate: string | null; capacity: string | null };
};

export type VehicleSubrentalCreation = {
  locationId: string;
  vendorId: string | null;
  vendorName: string | null;
  notes: string | null;
  lines: VehicleSubrentalLine[];
};

export type VehicleSubrentalCreated = { purchaseOrderId: string | null; subrentalIds: string[]; documentNumber: string | null };

/**
 * Creates temporary fleet vehicle rentals through the existing vendor-rental workflow (the same
 * request the Vendor Rental page sent), keeping the ids it returns so the new record can open.
 */
export async function createVehicleSubrental(input: VehicleSubrentalCreation): Promise<VehicleSubrentalCreated> {
  const fallback = 'The vehicle subrental could not be created.';
  let response: Response;
  try {
    response = await apiFetch('/workflows/fulfillment/xrental-order', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        locationId: input.locationId,
        vendorId: input.vendorId,
        vendorName: input.vendorName,
        notes: input.notes,
        lines: input.lines.map((line) => ({
          description: line.description,
          qty: line.qty,
          dailyCost: line.dailyCost,
          startAt: line.startAt,
          endAt: line.endAt,
          isFleetVehicle: true,
          vehicleInfo: line.vehicleInfo,
          notes: line.notes,
          itemId: null,
          productId: null,
          availabilityScope: 'location_pool',
          rentalTerm: 'short_term_pool',
          identifierMode: 'optional',
        })),
      }),
    });
  } catch (error) {
    throw toAppError(error, fallback, 'workflows.request');
  }
  const body = asObject(await response.json().catch(() => null));
  if (!response.ok || !body || body.ok === false) {
    const message = asText(body?.error);
    throw message ? new Error(message) : toAppError({ status: response.status }, fallback, 'workflows.request');
  }
  const purchaseOrder = asObject(body.purchaseOrder);
  return {
    purchaseOrderId: asText(purchaseOrder?.po_id),
    subrentalIds: asObjects(body.subrentals).map((row) => asText(row.subrental_id)).filter((id): id is string => Boolean(id)),
    documentNumber: asText(body.crossRentalDocNo) ?? asText(purchaseOrder?.doc_no),
  };
}

// ---------------------------------------------------------------------------------------------
// Portal labels
// ---------------------------------------------------------------------------------------------

/** One printable label file per 100 units (the label document's limit), named after the record. */
export async function downloadSubrentalUnitLabels(units: SubrentalRecordUnit[], fileStem: string): Promise<number> {
  const borrowed: BorrowedUnit[] = units
    .filter((unit) => unit.label)
    .map((unit) => ({
      unitId: unit.id,
      vendorBarcode: unit.vendorTag,
      portalBarcode: unit.label as string,
      status: unit.status === 'issued' || unit.status === 'returned' || unit.status === 'lost' || unit.status === 'expected' ? unit.status : 'received',
      receivedAt: null,
      returnedToVendorAt: null,
    }));
  if (!borrowed.length) throw new Error('No units on this subrental have Portal labels yet.');
  const { downloadVendorInventoryLabels } = await import('./vendorInventoryLabels');
  const chunks = Math.ceil(borrowed.length / 100);
  for (let index = 0; index < chunks; index += 1) {
    const part = borrowed.slice(index * 100, (index + 1) * 100);
    await downloadVendorInventoryLabels(part, chunks > 1 ? `${fileStem}-${index + 1}` : fileStem);
  }
  return chunks;
}
